<script setup lang="ts">
import { Eye, MapPin, MessageSquare, Plus } from 'lucide-vue-next'
const { t } = useI18n()
const router = useRouter()
const { stores, moduleAccessesByStores, user } = useLocalStorage()

const storesWithEReputation = computed(() => {
  return stores.value.filter((store) => moduleAccessesByStores.value[store.id]?.includes('e-reputation'))
})

const storesWithReviewsOnly = computed(() => {
  return stores.value.filter(
    (store) =>
      moduleAccessesByStores.value[store.id]?.includes('review') &&
      !moduleAccessesByStores.value[store.id]?.includes('e-reputation')
  )
})

const shouldShowIntermediatePage = computed(() => {
  return storesWithEReputation.value.length > 0 && storesWithReviewsOnly.value.length > 0
})

const navigateToReviews = () => {
  router.push({ name: 'ReviewList' })
}
const navigateToPartoo = () => {
  router.push({ name: 'EReputation', params: { page: 'stores' } })
}

onMounted(() => {
  if (shouldShowIntermediatePage.value) return

  if (storesWithEReputation.value.length > 0) {
    router.replace({ name: 'EReputation', params: { page: 'stores' } })
  } else if (storesWithReviewsOnly.value.length > 0) {
    router.replace({ name: 'ReviewList' })
  }
})
</script>

<template>
  <div class="min-h-screen w-full flex flex-row justify-center items-center p-5">
    <div class="flex flex-col gap-10 max-w-4xl w-full">
      <div class="flex flex-col gap-5">
        <p class="text-center text-lg text-foreground-secondary">
          <i18n-t keypath="pages.eReputation.greeting" tag="span">
            <template #name>
              <b>{{ user.name }}</b>
            </template>
            <template #count>
              <b>{{ storesWithEReputation.length + storesWithReviewsOnly.length }}</b>
            </template>
          </i18n-t>
          <br />
          {{ t('pages.eReputation.differentModules') }}
        </p>
      </div>
      <div class="flex flex-col-reverse lg:flex-row lg:gap-10 gap-4 w-full">
        <Card class="flex-1 rounded-3xl! p-8">
          <CardHeader>
            <div class="flex items-center justify-center mb-2">
              <div class="flex flex-col rounded-2xl bg-muted p-3 text-center">
                <Eye class="size-6 text-muted-foreground" />
              </div>
            </div>
            <CardTitle class="text-center text-xl">{{ t('pages.eReputation.viewReviews') }}</CardTitle>
          </CardHeader>
          <CardContent class="flex flex-1 flex-col gap-3 rounded-xl border border-border bg-muted p-5">
            <p class="text-[11px] font-semibold uppercase text-muted-foreground">
              {{ t('pages.eReputation.storesConcerned') }}
            </p>
            <div
              v-for="store in storesWithReviewsOnly.slice(0, 3)"
              :key="store.id"
              class="flex flex-row items-center gap-3"
            >
              <div class="flex size-6 items-center justify-center rounded-lg border bg-white">
                <MapPin class="size-4 text-foreground-secondary" />
              </div>
              <p class="text-sm">{{ store.name }}</p>
            </div>
            <div v-if="storesWithReviewsOnly.length > 3" class="flex flex-row items-center gap-3">
              <div class="flex size-6 items-center justify-center rounded-lg border bg-white">
                <Plus class="size-4 text-foreground-secondary" />
              </div>
              <StoreTooltip
                :stores="storesWithReviewsOnly.slice(3)"
                :available-store-ids="storesWithReviewsOnly.slice(3).map((store) => store.id)"
                position="top-start"
                detail-expandable
              />
            </div>
          </CardContent>
          <CardFooter class="px-0">
            <Button variant="outline" size="lg" class="w-full" @click="navigateToReviews()">
              {{ t('pages.eReputation.openReviews') }}
            </Button>
          </CardFooter>
        </Card>

        <Card class="flex-1 rounded-3xl! p-8">
          <CardHeader>
            <div class="flex items-center justify-center mb-2">
              <div class="flex flex-col rounded-2xl bg-primary/5 p-3 text-center">
                <MessageSquare class="size-6 text-primary" />
              </div>
            </div>
            <CardTitle class="text-center text-xl">{{ t('pages.eReputation.manageReputation') }}</CardTitle>
          </CardHeader>
          <CardContent class="flex flex-1 flex-col gap-3 rounded-xl border border-border bg-muted p-5">
            <p class="text-[11px] font-semibold uppercase text-muted-foreground">
              {{ t('pages.eReputation.storesConcerned') }}
            </p>
            <div
              v-for="store in storesWithEReputation.slice(0, 3)"
              :key="store.id"
              class="flex flex-row items-center gap-3"
            >
              <div class="flex size-6 items-center justify-center rounded-lg border bg-white">
                <MapPin class="size-4 text-foreground-secondary" />
              </div>
              <p class="text-sm">{{ store.name }}</p>
            </div>
            <div v-if="storesWithEReputation.length > 3" class="flex flex-row items-center gap-3">
              <div class="flex size-6 items-center justify-center rounded-lg border bg-white">
                <Plus class="size-4 text-foreground-secondary" />
              </div>
              <StoreTooltip
                :stores="storesWithEReputation.slice(3)"
                :available-store-ids="storesWithEReputation.slice(3).map((store) => store.id)"
                position="top-start"
                detail-expandable
              />
            </div>
          </CardContent>
          <CardFooter class="px-0">
            <Button variant="default-primary" size="lg" class="w-full" @click="navigateToPartoo()">
              {{ t('pages.eReputation.openAdvanced') }}
            </Button>
          </CardFooter>
        </Card>
      </div>
      <p class="text-center text-muted-foreground text-xs">{{ t('pages.eReputation.switchInterface') }}</p>
    </div>
  </div>
</template>
